import { useCallback, useMemo } from 'react';
import { Button, Grid, Stack } from 'components/ui';
import { AuthPasswordField, AuthSocialButtons, AuthTextField } from 'pages/Auth/components';
import { useAuthInitialFocus } from 'pages/Auth/hooks';
import {
  SIGN_UP_CONFIRM_PASSWORD_LABEL,
  SIGN_UP_CONFIRM_PASSWORD_PLACEHOLDER,
  SIGN_UP_EMAIL_LABEL,
  SIGN_UP_EMAIL_MAX_LENGTH,
  SIGN_UP_EMAIL_PLACEHOLDER,
  SIGN_UP_FIRST_NAME_LABEL,
  SIGN_UP_FIRST_NAME_MAX_LENGTH,
  SIGN_UP_FIRST_NAME_PLACEHOLDER,
  SIGN_UP_LAST_NAME_LABEL,
  SIGN_UP_LAST_NAME_MAX_LENGTH,
  SIGN_UP_LAST_NAME_PLACEHOLDER,
  SIGN_UP_PASSWORD_LABEL,
  SIGN_UP_PASSWORD_PLACEHOLDER,
  SIGN_UP_SUBMIT_LABEL,
} from 'pages/Auth/SignUp/SignUp.constants';
import { SIGN_UP_TEST_IDS } from 'pages/Auth/SignUp/SignUp.testIds';
import { getAuthPasswordFieldHelperText, getVisibleFieldErrorMessage } from 'pages/Auth/utils';
import { SignUpFieldErrorOverrides, SignUpFormStepProps } from './SignUpFormStep.props';

const getOverrideFieldError = (
  fieldErrorOverrides: SignUpFieldErrorOverrides | undefined,
  fieldName: keyof SignUpFieldErrorOverrides
) => fieldErrorOverrides?.[fieldName];

const SignUpFormStep = ({
  fieldErrorOverrides,
  form,
  isSocialPending,
  isSubmitting,
  onGoogleLogin,
  onMicrosoftLogin,
  onSubmit,
  socialProviderPending,
  turnstileHostRef,
}: SignUpFormStepProps) => {
  const isBusy = isSubmitting || isSocialPending;
  const watchedPassword = form.watch('password');
  const hasOverrideErrors = Boolean(fieldErrorOverrides && Object.keys(fieldErrorOverrides).length > 0);
  const hasFieldErrors = Object.keys(form.formState.errors).length > 0 || hasOverrideErrors;
  const isSubmitDisabled = isBusy || !form.formState.isValid || hasFieldErrors;
  const focusFirstNameField = useCallback(() => {
    form.setFocus('first_name');
  }, [form]);
  const firstNameErrorMessage = getOverrideFieldError(fieldErrorOverrides, 'first_name')
    ?? getVisibleFieldErrorMessage({ error: form.formState.errors.first_name, hiddenErrorTypes: ['alert'] });
  const lastNameErrorMessage = getOverrideFieldError(fieldErrorOverrides, 'last_name')
    ?? getVisibleFieldErrorMessage({ error: form.formState.errors.last_name, hiddenErrorTypes: ['alert'] });
  const emailErrorMessage = getOverrideFieldError(fieldErrorOverrides, 'email')
    ?? getVisibleFieldErrorMessage({ error: form.formState.errors.email, hiddenErrorTypes: ['alert'] });
  const passwordOverrideErrorMessage = getOverrideFieldError(fieldErrorOverrides, 'password');
  const passwordConfirmationErrorMessage = getOverrideFieldError(fieldErrorOverrides, 're_password')
    ?? getVisibleFieldErrorMessage({ error: form.formState.errors.re_password, hiddenErrorTypes: ['alert'] });
  const passwordHelperText = useMemo(
    () => passwordOverrideErrorMessage ?? getAuthPasswordFieldHelperText({
      error: form.formState.errors.password,
      hiddenErrorTypes: ['alert'],
      password: watchedPassword,
    }),
    [form.formState.errors.password, passwordOverrideErrorMessage, watchedPassword]
  );

  useAuthInitialFocus(focusFirstNameField, !isBusy);

  return (
    <Stack component="form" noValidate onSubmit={onSubmit} spacing={2.25}>
      <Grid container spacing={2}>
        <Grid size={{ sm: 6, xs: 12 }}>
          <AuthTextField
            disabled={isBusy}
            error={Boolean(firstNameErrorMessage) || Boolean(form.formState.errors.first_name)}
            helperText={firstNameErrorMessage}
            id="auth-sign-up-first-name"
            slotProps={{
              htmlInput: { maxLength: SIGN_UP_FIRST_NAME_MAX_LENGTH },
            }}
            label={SIGN_UP_FIRST_NAME_LABEL}
            placeholder={SIGN_UP_FIRST_NAME_PLACEHOLDER}
            {...form.register('first_name')}
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }}>
          <AuthTextField
            disabled={isBusy}
            error={Boolean(lastNameErrorMessage) || Boolean(form.formState.errors.last_name)}
            helperText={lastNameErrorMessage}
            id="auth-sign-up-last-name"
            slotProps={{
              htmlInput: { maxLength: SIGN_UP_LAST_NAME_MAX_LENGTH },
            }}
            label={SIGN_UP_LAST_NAME_LABEL}
            placeholder={SIGN_UP_LAST_NAME_PLACEHOLDER}
            {...form.register('last_name')}
          />
        </Grid>
      </Grid>
      <AuthTextField
        autoComplete="email"
        disabled={isBusy}
        error={Boolean(emailErrorMessage) || Boolean(form.formState.errors.email)}
        helperText={emailErrorMessage}
        id="auth-sign-up-email"
        slotProps={{
          htmlInput: { maxLength: SIGN_UP_EMAIL_MAX_LENGTH },
        }}
        label={SIGN_UP_EMAIL_LABEL}
        placeholder={SIGN_UP_EMAIL_PLACEHOLDER}
        {...form.register('email')}
      />
      <AuthPasswordField
        autoComplete="new-password"
        disabled={isBusy}
        error={Boolean(passwordOverrideErrorMessage) || Boolean(form.formState.errors.password)}
        helperText={passwordHelperText}
        id="auth-sign-up-password"
        label={SIGN_UP_PASSWORD_LABEL}
        placeholder={SIGN_UP_PASSWORD_PLACEHOLDER}
        {...form.register('password')}
      />
      <AuthPasswordField
        autoComplete="new-password"
        disabled={isBusy}
        error={Boolean(passwordConfirmationErrorMessage) || Boolean(form.formState.errors.re_password)}
        helperText={passwordConfirmationErrorMessage}
        id="auth-sign-up-confirm-password"
        label={SIGN_UP_CONFIRM_PASSWORD_LABEL}
        placeholder={SIGN_UP_CONFIRM_PASSWORD_PLACEHOLDER}
        {...form.register('re_password')}
      />
      <Button
        data-testid={SIGN_UP_TEST_IDS.submitButton}
        disabled={isSubmitDisabled}
        loading={isSubmitting}
        type="submit"
        variant="contained"
        visualVariant="positive"
      >
        {SIGN_UP_SUBMIT_LABEL}
      </Button>
      <AuthSocialButtons
        disabled={isBusy}
        onGoogleLogin={onGoogleLogin}
        onMicrosoftLogin={onMicrosoftLogin}
        socialProviderPending={socialProviderPending}
      />
      <div data-testid="auth-sign-up-turnstile-host" ref={turnstileHostRef} style={{ width: '100%' }} />
    </Stack>
  );
};

export default SignUpFormStep;
